import { useId, type ReactNode } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './State.module.css';

// Empty state — DESIGN_SYSTEM §12 row 27 (`.empty`) and REQ-287 AC2: first-use · filtered-to-nothing ·
// all-clear. An icon tile, a reassuring one-line title, one sentence that says what will appear here, and
// the next step. The words are the screen's (props); this is the frame. Test id `state.empty` by default.

export interface EmptyStateProps {
  icon: IconName;
  title: string;
  /** One sentence: what will appear here, or why it is empty. */
  body?: ReactNode;
  /** One primary action, maybe a ghost beside it. */
  action?: ReactNode;
  /** Less vertical padding, for a panel or a card inside a page. */
  compact?: boolean;
  /**
   * Where the title sits in the page's outline. An empty state that REPLACES a page's main content follows
   * that page's `<h1>` directly and must be an `<h2>`; one inside a panel that already has a heading stays
   * an `<h3>` (REQ-281 / WCAG 2.2 "heading-order"). The words and the size do not change either way.
   */
  headingLevel?: 2 | 3;
  testID?: string;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  compact = false,
  headingLevel = 3,
  testID = 'state.empty',
  className,
}: EmptyStateProps) {
  const titleId = useId();
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className={cx(styles.state, compact && styles.compact, className)}
      data-testid={testID}
    >
      <span className={styles.art}>
        <Icon name={icon} size={24} />
      </span>
      <Heading id={titleId} className={styles.title}>
        {title}
      </Heading>
      {body ? <p className={styles.body}>{body}</p> : null}
      {action ? <div className={styles.actions}>{action}</div> : null}
    </div>
  );
}
